import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Coordinate } from "@swagger/typescript-fetch-goflyer";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { adminApiService } from "../../../utils/api";
import { toastSuccess, toastError } from "../../../utils/pageUtil/toast";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";

interface CoordinatesModalProps {
  open: boolean;
  onClose: () => void;
  coordinates: Coordinate[];
  itemName?: string;
  componentPositionId?: string;
  onUpdated?: () => void;
}

export const CoordinatesModal: React.FC<CoordinatesModalProps> = ({
  open,
  onClose,
  coordinates,
  itemName,
  componentPositionId,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();
  const [editedCoordinates, setEditedCoordinates] = useState<Coordinate[]>([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [updateMode, setUpdateMode] = useState<"linked" | "individual">(
    "linked"
  );

  React.useEffect(() => {
    if (open && coordinates) {
      setEditedCoordinates(JSON.parse(JSON.stringify(coordinates)));
      setHasChanges(false);
    }
  }, [open, coordinates]);

  const handleNameChange = (index: number, newName: string) => {
    const updated = [...editedCoordinates];

    if (updateMode === "linked") {
      const targetComponentId = updated[index].component?.id;
      if (targetComponentId) {
        updated.forEach((coord, idx) => {
          if (coord.component?.id === targetComponentId) {
            updated[idx] = {
              ...updated[idx],
              component: {
                ...updated[idx].component,
                name: newName,
              },
            };
          }
        });
      }
    } else {
      updated[index] = {
        ...updated[index],
        component: {
          ...updated[index].component,
          name: newName,
        },
      };
    }

    setEditedCoordinates(updated);

    const hasAnyChanges = updated.some(
      (coord, idx) =>
        coord.component?.name !== coordinates[idx]?.component?.name
    );
    setHasChanges(hasAnyChanges);
  };

  const handleUpdateClick = () => {
    if (!hasChanges) return;
    setIsConfirmModalOpen(true);
  };

  const getChangedComponents = () => {
    return editedCoordinates
      .map((coord, idx) => ({
        index: idx,
        oldName: coordinates[idx]?.component?.name || "",
        newName: coord.component?.name || "",
      }))
      .filter((item) => item.oldName !== item.newName);
  };

  const handleConfirmUpdate = async () => {
    if (!componentPositionId) return;

    setIsUpdating(true);
    try {
      await adminApiService.updateOneBaseGfFlyerItemComponentPositionsAdminControllerGfFlyerItemComponentPosition(
        {
          coordinates: editedCoordinates,
        },
        componentPositionId,
        authHeaders
      );

      toastSuccess(t(messages.Updated()));
      setIsConfirmModalOpen(false);
      setHasChanges(false);

      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      console.error("Error updating component names:", error);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelConfirm = () => {
    setIsConfirmModalOpen(false);
  };

  const handleClose = () => {
    setEditedCoordinates([]);
    setHasChanges(false);
    setIsConfirmModalOpen(false);
    setIsUpdating(false);
    setUpdateMode("linked");
    onClose();
  };

  const handleModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: "linked" | "individual" | null
  ) => {
    if (newMode !== null) {
      setUpdateMode(newMode);
    }
  };

  const getLinkedComponentsCount = (componentId: string) => {
    return editedCoordinates.filter(
      (coord) => coord.component?.id === componentId
    ).length;
  };

  const getComponentGroupColor = (componentId: string) => {
    const colors = [
      "#f58220",
      "#2196F3",
      "#4CAF50",
      "#9C27B0",
      "#FF5722",
      "#00BCD4",
      "#FFC107",
      "#E91E63",
      "#009688",
      "#FF9800",
      "#673AB7",
      "#3F51B5",
    ];

    const uniqueIds = Array.from(
      new Set(
        editedCoordinates
          .map((coord) => coord.component?.id)
          .filter((id) => id !== undefined)
      )
    );

    const index = uniqueIds.indexOf(componentId);
    return colors[index % colors.length];
  };

  const changedComponents = getChangedComponents();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      data-testid="coordinates-modal"
    >
      <DialogTitle
        data-testid="coordinates-modal-title"
        sx={{
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          background: "linear-gradient(to right, #f8f9fa, #ffffff)",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight="600"
              sx={{
                color: "#1a1a1a",
                mb: 0.5,
                fontSize: "1.25rem",
              }}
            >
              Component Coordinates
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              {itemName}
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={updateMode}
            exclusive
            onChange={handleModeChange}
            size="small"
            data-testid="update-mode-toggle"
            sx={{
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <ToggleButton
              value="linked"
              data-testid="linked-mode-button"
              sx={{
                px: 2,
                py: 0.75,
                border: "none",
                fontWeight: 500,
                fontSize: "0.813rem",
                textTransform: "none",
                "&.Mui-selected": {
                  backgroundColor: "#f58220",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#e57514",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(245, 130, 32, 0.08)",
                },
              }}
            >
              <LinkIcon fontSize="small" sx={{ mr: 0.75 }} />
              Linked
            </ToggleButton>
            <ToggleButton
              value="individual"
              data-testid="individual-mode-button"
              sx={{
                px: 2,
                py: 0.75,
                border: "none",
                fontWeight: 500,
                fontSize: "0.813rem",
                textTransform: "none",
                "&.Mui-selected": {
                  backgroundColor: "#f58220",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#e57514",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(245, 130, 32, 0.08)",
                },
              }}
            >
              <LinkOffIcon fontSize="small" sx={{ mr: 0.75 }} />
              Individual
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {updateMode === "linked" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1,
              backgroundColor: "rgba(245, 130, 32, 0.08)",
              borderRadius: "6px",
              border: "1px solid rgba(245, 130, 32, 0.2)",
            }}
          >
            <LinkIcon sx={{ fontSize: "1rem", color: "#f58220" }} />
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                fontSize: "0.813rem",
                fontWeight: 500,
              }}
            >
              Components with the same ID will be updated together
            </Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent
        data-testid="coordinates-modal-content"
        sx={{
          p: 0,
          backgroundColor: "#fafafa",
        }}
      >
        {coordinates && coordinates.length > 0 ? (
          <Box
            sx={{
              maxHeight: "70vh",
              overflow: "auto",
            }}
            data-testid="coordinates-table-container"
          >
            <TableContainer
              component={Paper}
              data-testid="coordinates-table-wrapper"
              elevation={0}
              sx={{
                backgroundColor: "transparent",
              }}
            >
              <Table
                stickyHeader
                data-testid="coordinates-table"
                sx={{
                  "& .MuiTableCell-root": {
                    borderBottom: "1px solid #e8e8e8",
                  },
                }}
              >
                <TableHead data-testid="coordinates-table-head">
                  <TableRow
                    data-testid="coordinates-table-header-row"
                    sx={{
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <TableCell
                      data-testid="header-index"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell
                      data-testid="header-component-name"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {t(messages["Component_Name"]())}
                    </TableCell>
                    <TableCell
                      data-testid="header-flyer-page"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {t(messages["Flyer_Page"]())}
                    </TableCell>
                    <TableCell
                      data-testid="header-x"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      X
                    </TableCell>
                    <TableCell
                      data-testid="header-y"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      Y
                    </TableCell>
                    <TableCell
                      data-testid="header-width"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {t(messages["Width"]())}
                    </TableCell>
                    <TableCell
                      data-testid="header-height"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {t(messages["Height"]())}
                    </TableCell>
                    <TableCell
                      data-testid="header-component-id"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.813rem",
                        color: "#666",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        py: 2,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {t(messages["Component_ID"]())}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-testid="coordinates-table-body">
                  {editedCoordinates.map((coordinate, index) => (
                    <TableRow
                      key={index}
                      data-testid={`coordinate-row-${index}`}
                      sx={{
                        backgroundColor:
                          coordinate.component?.name !==
                          coordinates[index]?.component?.name
                            ? "rgba(245, 130, 32, 0.08)"
                            : "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            coordinate.component?.name !==
                            coordinates[index]?.component?.name
                              ? "rgba(245, 130, 32, 0.12)"
                              : "rgba(0, 0, 0, 0.02)",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        },
                      }}
                    >
                      <TableCell
                        data-testid={`coordinate-index-${index}`}
                        sx={{
                          py: 2.5,
                          fontSize: "0.875rem",
                          color: "#666",
                          fontWeight: 500,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Box
                            sx={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              backgroundColor: "rgba(245, 130, 32, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.813rem",
                              fontWeight: 600,
                              color: "#f58220",
                            }}
                          >
                            {index + 1}
                          </Box>
                          {updateMode === "linked" &&
                            coordinate.component?.id &&
                            getLinkedComponentsCount(coordinate.component.id) >
                              1 && (
                              <Chip
                                size="small"
                                label={getLinkedComponentsCount(
                                  coordinate.component.id
                                )}
                                sx={{
                                  height: "22px",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  backgroundColor: getComponentGroupColor(
                                    coordinate.component.id
                                  ),
                                  color: "white",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                                  "& .MuiChip-label": {
                                    px: 0.75,
                                  },
                                }}
                                icon={
                                  <LinkIcon
                                    sx={{
                                      fontSize: "0.875rem",
                                      color: "white !important",
                                    }}
                                  />
                                }
                              />
                            )}
                        </Box>
                      </TableCell>
                      <TableCell
                        data-testid={`coordinate-component-name-${index}`}
                        sx={{ py: 2 }}
                      >
                        <TextField
                          size="small"
                          value={coordinate.component?.name || ""}
                          onChange={(e) =>
                            handleNameChange(index, e.target.value)
                          }
                          fullWidth
                          disabled={isUpdating}
                          data-testid={`coordinate-name-edit-input-${index}`}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "white",
                              borderRadius: "6px",
                              fontSize: "0.875rem",
                              transition: "all 0.2s ease",
                              "& fieldset": {
                                borderColor: "#e0e0e0",
                              },
                              "&:hover fieldset": {
                                borderColor: "#f58220",
                              },
                              "&.Mui-focused": {
                                boxShadow: "0 0 0 3px rgba(245, 130, 32, 0.1)",
                                "& fieldset": {
                                  borderColor: "#f58220",
                                  borderWidth: "2px",
                                },
                              },
                            },
                            "& .MuiInputBase-input": {
                              py: 1.25,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        data-testid={`coordinate-flyer-page-${index}`}
                        sx={{
                          py: 2.5,
                          fontSize: "0.875rem",
                          color: "#444",
                        }}
                      >
                        {coordinate.flyerPage || "-"}
                      </TableCell>
                      <TableCell
                        data-testid={`coordinate-x-${index}`}
                        sx={{
                          py: 2.5,
                          fontSize: "0.875rem",
                          color: "#444",
                          fontFamily: "monospace",
                        }}
                      >
                        {coordinate.x}
                      </TableCell>
                      <TableCell
                        data-testid={`coordinate-y-${index}`}
                        sx={{
                          py: 2.5,
                          fontSize: "0.875rem",
                          color: "#444",
                          fontFamily: "monospace",
                        }}
                      >
                        {coordinate.y}
                      </TableCell>
                      <TableCell
                        data-testid={`coordinate-width-${index}`}
                        sx={{
                          py: 2.5,
                          fontSize: "0.875rem",
                          color: "#444",
                          fontFamily: "monospace",
                        }}
                      >
                        {coordinate.component?.width}
                      </TableCell>
                      <TableCell
                        data-testid={`coordinate-height-${index}`}
                        sx={{
                          py: 2.5,
                          fontSize: "0.875rem",
                          color: "#444",
                          fontFamily: "monospace",
                        }}
                      >
                        {coordinate.component?.height}
                      </TableCell>
                      <TableCell
                        data-testid={`coordinate-component-id-${index}`}
                        sx={{
                          py: 2.5,
                          fontSize: "0.813rem",
                          color: "#888",
                          fontFamily: "monospace",
                        }}
                      >
                        {coordinate.component?.id}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Typography variant="body1" data-testid="coordinates-empty-message">
            {t(messages["No_coordinates_available"]())}
          </Typography>
        )}
      </DialogContent>
      <DialogActions
        data-testid="coordinates-modal-actions"
        sx={{
          borderTop: "1px solid #e0e0e0",
          p: 3,
          backgroundColor: "#f8f9fa",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Box>
            {hasChanges && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: "rgba(245, 130, 32, 0.1)",
                  borderRadius: "6px",
                  border: "1px solid rgba(245, 130, 32, 0.3)",
                }}
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#f58220",
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.5 },
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#f58220",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {changedComponents.length} component
                  {changedComponents.length !== 1 ? "s" : ""} modified
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" gap={2}>
            <Button
              onClick={handleClose}
              variant="outlined"
              data-testid="coordinates-modal-close-button"
              disabled={isUpdating}
              sx={{
                px: 3,
                py: 1.25,
                fontSize: "0.875rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                color: "#d32f2f",
                borderColor: "#d32f2f",
                borderWidth: "1.5px",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#c62828",
                  borderWidth: "1.5px",
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 8px rgba(211, 47, 47, 0.2)",
                },
              }}
            >
              {t(messages["Close"]())}
            </Button>
            <Button
              onClick={handleUpdateClick}
              variant="contained"
              disabled={!hasChanges || isUpdating}
              data-testid="coordinates-modal-update-button"
              sx={{
                px: 4,
                py: 1.25,
                fontSize: "0.875rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#f58220",
                boxShadow: "0 4px 12px rgba(245, 130, 32, 0.3)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#e57514",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(245, 130, 32, 0.4)",
                },
                "&:disabled": {
                  backgroundColor: "rgba(245, 130, 32, 0.3)",
                  color: "rgba(255, 255, 255, 0.6)",
                  boxShadow: "none",
                },
              }}
            >
              {isUpdating ? "Updating..." : t(messages["Update"]())}
            </Button>
          </Box>
        </Box>
      </DialogActions>

      <MuiConfirmationModal
        open={isConfirmModalOpen}
        handleCancel={handleCancelConfirm}
        handleConfirm={handleConfirmUpdate}
        title={`${t(messages.Update())} Confirmation`}
        description={
          <>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to update {changedComponents.length}{" "}
              component name(s)?
            </Typography>
            <Box mt={2} p={2} bgcolor="warning.light" borderRadius={1}>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="warning.dark"
              >
                ⚠️ Warning
              </Typography>
              <Typography variant="body2" mt={1}>
                Please make sure you are updating the correct components.
              </Typography>
              <Box mt={2} sx={{ maxHeight: "200px", overflow: "auto" }}>
                {changedComponents.map((item, idx) => (
                  <Box key={idx} mb={1} p={1} bgcolor="white" borderRadius={1}>
                    <Typography variant="body2">
                      <strong>#{item.index + 1}</strong>
                    </Typography>
                    <Typography variant="body2" fontSize="0.85rem">
                      <strong>Old:</strong> {item.oldName || "(empty)"}
                    </Typography>
                    <Typography variant="body2" fontSize="0.85rem">
                      <strong>New:</strong> {item.newName || "(empty)"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        }
      />
    </Dialog>
  );
};
